/**
 * MaturityLevelBadge - Colored badge displaying maturity level
 *
 * A foundational shared component used throughout the CISA assessment UI.
 * Displays TRADITIONAL, INITIAL, ADVANCED, or OPTIMAL with appropriate colors.
 */

import React from 'react';
import { MATURITY_LEVELS } from '../../../../config/zeroTrustConstants';
import './MaturityLevelBadge.css';

export default function MaturityLevelBadge({
  level,
  size = 'md', // 'sm' | 'md' | 'lg'
  variant = 'filled', // 'filled' | 'outline' | 'subtle'
  showLabel = true,
  animated = false,
  className = ''
}) {
  const levelData = MATURITY_LEVELS.find(m => m.key === level) || MATURITY_LEVELS[0];
  const levelKey = levelData.key.toLowerCase();

  return (
    <span
      className={`
        mlb-badge
        mlb-badge--${levelKey}
        mlb-badge--${size}
        mlb-badge--${variant}
        ${animated ? 'mlb-badge--animated' : ''}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      style={{ '--mlb-color': levelData.color }}
    >
      {showLabel ? levelData.label : levelData.short}
    </span>
  );
}

/**
 * MaturityLevelDot - A small colored dot indicator
 */
export function MaturityLevelDot({ level, size = 'md', animated = false }) {
  const levelData = MATURITY_LEVELS.find(m => m.key === level) || MATURITY_LEVELS[0];
  const levelKey = levelData.key.toLowerCase();

  return (
    <span
      className={`
        mlb-dot
        mlb-dot--${levelKey}
        mlb-dot--${size}
        ${animated ? 'mlb-dot--animated' : ''}
      `.trim().replace(/\s+/g, ' ')}
      style={{ '--mlb-color': levelData.color }}
      title={levelData.label}
    />
  );
}

/**
 * MaturityProgressBar - Shows current level as a progress indicator
 */
export function MaturityProgressBar({
  currentLevel,
  targetLevel = null,
  animated = true,
  showLabels = false
}) {
  const currentIndex = MATURITY_LEVELS.findIndex(m => m.key === currentLevel);
  const targetIndex = targetLevel ? MATURITY_LEVELS.findIndex(m => m.key === targetLevel) : -1;
  const progress = ((currentIndex + 1) / MATURITY_LEVELS.length) * 100;

  return (
    <div className="mlb-progress">
      <div className="mlb-progress-track">
        {MATURITY_LEVELS.map((level, idx) => (
          <div
            key={level.key}
            className={`
              mlb-progress-segment
              mlb-progress-segment--${level.key.toLowerCase()}
              ${idx <= currentIndex ? 'mlb-progress-segment--filled' : ''}
              ${targetIndex >= 0 && idx > currentIndex && idx <= targetIndex ? 'mlb-progress-segment--target' : ''}
            `.trim().replace(/\s+/g, ' ')}
            style={{ '--segment-color': level.color }}
          />
        ))}
        <div
          className={`mlb-progress-fill ${animated ? 'mlb-progress-fill--animated' : ''}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      {showLabels && (
        <div className="mlb-progress-labels">
          {MATURITY_LEVELS.map(level => (
            <span key={level.key} className="mlb-progress-label">
              {level.short}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
